import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { applySeo } from './applySeo'
import { normalizePath, routeSeoIndex } from './tags'
import type { SeoConfig, SeoMeta, SeoSite } from './types'

/**
 * Sets a page's metadata, for a page that knows something the site tree cannot.
 *
 * A detail route — `/blog/:slug`, `/shop/:slug` — has one entry in `App.tsx` and hundreds
 * of pages, so its title and its structured data can only be built where the item is
 * known. That is here.
 *
 * `site` accepts `undefined` so a shared component can take it as an optional prop and
 * still call this unconditionally — `NotFound` does exactly that. A hook that some apps
 * call and others do not is not a hook.
 *
 * The dependency is the serialised meta rather than the object, so a caller may build the
 * object inline in the render body — which every page does — without re-writing the head
 * on every keystroke elsewhere on the page.
 *
 * This runs in a **passive** effect, and `useRouteSeo` runs in a layout effect, so a page
 * always writes after the shell has written the route's default. That is the whole
 * override mechanism; see `useRouteSeo`.
 */
export function useSeo(site: SeoSite | undefined, meta: SeoMeta): void {
  const key = JSON.stringify(meta)

  useEffect(() => {
    if (!site) return
    applySeo(site, meta)
    // `meta` is deliberately absent from the dependency list — `key` is its serialisation,
    // and listing both would re-run the effect on every render that rebuilt the object.
  }, [site, key])
}

/**
 * Applies the site tree's metadata on every navigation. Mounted once, by `AppShell`.
 *
 * Two things here are load-bearing.
 *
 * **It matches the path exactly and does nothing otherwise.** A route with no entry is a
 * page the tree could not enumerate, and writing a default for it would put the wrong
 * title on it rather than no title.
 *
 * **It writes in a layout effect, and `useSeo` writes in a passive one.** React runs a
 * child's effects before its parent's, so a page setting its own metadata would otherwise
 * be overwritten by the shell a moment later. Layout effects all run before any passive
 * effect, so this ordering is what lets a page refine what the tree says about its route
 * — `/book` marking a *filtered* result set `noindex` while the bare route stays indexed —
 * instead of having to be left out of the tree to escape the shell.
 */
export function useRouteSeo(config: SeoConfig | undefined): void {
  const { pathname } = useLocation()
  const routes = config?.routes
  const index = useMemo(() => (routes ? routeSeoIndex(routes) : null), [routes])

  useLayoutEffect(() => {
    if (!config || !index) return
    const meta = index[normalizePath(pathname)]
    if (meta) applySeo(config.site, meta)
  }, [config, index, pathname])
}
